import React, { useState, useCallback } from 'react';
import { CloudUpload, CloudOff } from '@material-ui/icons';
import api from '../../../../../../services/api';
import OAuthConnectionMonitor from '../OAuthConnectorMonitor';
import {
  ConnectorContainer,
  ConnectedStatus,
  DisconnectedStatus,
  BotaoOAuthConnect,
  BotaoOAuthDisconnect,
  StatusButtonRow,
  ButtonContainer,
} from './styles';

interface OAuthConnectorProps {
  accountId: string | number;
  isConnected: boolean;
  onConnectionChange: (isConnected: boolean) => void;
  buttonText?: {
    connect: string;
    disconnect: string;
  };
  confirmMessages?: {
    connect: string;
    disconnect: string;
  };
}

const OAuthConnector: React.FC<OAuthConnectorProps> = ({
  accountId,
  isConnected,
  onConnectionChange,
  buttonText = {
    connect: 'Conectar a API BeerSales',
    disconnect: 'Desconectar da API BeerSales',
  },
  confirmMessages = {
    connect:
      'Tem certeza que deseja conectar a API BeerSales?\nIsso abrirá uma nova janela para autenticação.',
    disconnect:
      'Tem certeza que deseja desconectar da API BeerSales?\nTodas as integrações serão interrompidas.',
  },
}) => {
  const [showMonitor, setShowMonitor] = useState(false);
  const [connectionState, setConnectionState] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(false);

  // Função chamada quando a conexão for bem-sucedida
  const handleConnectionSuccess = useCallback(() => {
    // Atrasa ligeiramente a atualização do estado para garantir que o formulário esteja sincronizado
    setTimeout(() => {
      onConnectionChange(true);
    }, 0);
  }, [onConnectionChange]);

  const popupFeatures = 'width=600,height=700,top=100,left=100';

  const handleConnect = useCallback(async () => {
    try {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setIsConnecting(true);

      const { data } = await api.get<{ route: string; state: string }>(
        `/admin/beersales/get-authorization-code-route`,
        { params: { account_id: accountId } },
      );

      if (data.route && data.state) {
        setConnectionState(data.state);

        // Abre popup
        const popup = window.open(data.route, 'oauthWindow', popupFeatures);

        if (!popup) {
          // eslint-disable-next-line no-alert
          alert(
            'Não foi possível abrir a janela de autenticação. Verifique o bloqueador de pop-ups.',
          );
          return;
        }

        // Monitora o fechamento ou conclusão
        const timer = setInterval(async () => {
          if (popup.closed) {
            clearInterval(timer);
            setShowMonitor(false);
            setIsConnecting(false);
            return;
          }

          try {
            // Verifica se conseguimos ler a URL atual (mesmo domínio ou postMessage)
            if (popup.location.origin === window.location.origin) {
              // Aqui você pode verificar a URL para confirmar se o OAuth retornou sucesso
              // ou chamar sua API para validar pelo `state`
              clearInterval(timer);
              popup.close();
              handleConnectionSuccess();
              setShowMonitor(false);
            }
          } catch (e) {
            // Ignora erros de cross-origin até que retorne para o nosso domínio
          }
        }, 500);

        setShowMonitor(true);
      }
    } catch (error) {
      console.error('Erro ao conectar com API BeerSales:', error);
    } finally {
      setIsConnecting(false);
    }
  }, [accountId, handleConnectionSuccess]);

  // Função para desconectar da API BeerSales
  const handleDisconnect = useCallback(async () => {
    try {
      // Preservar dados do formulário atual antes de desconectar
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setIsConnecting(true);

      await api.post('/admin/beersales/disconnect', {
        account_id: accountId,
      });

      // Atualiza o estado de conexão
      onConnectionChange(false);
    } catch (error) {
      console.error('Erro ao desconectar da API BeerSales:', error);
    } finally {
      setIsConnecting(false);
    }
  }, [accountId, onConnectionChange]);

  return (
    <ConnectorContainer>
      <StatusButtonRow>
        {isConnected ? (
          <ConnectedStatus>API BeerSales conectada com sucesso</ConnectedStatus>
        ) : (
          <DisconnectedStatus>API BeerSales não conectada</DisconnectedStatus>
        )}

        <ButtonContainer>
          {!isConnected ? (
            <BotaoOAuthConnect
              titulo="Conectar API"
              mensagem={confirmMessages.connect}
              funcao={handleConnect}
              disabled={isConnecting}
            >
              <CloudUpload style={{ marginRight: '8px' }} />
              {isConnecting ? 'Conectando...' : buttonText.connect}
            </BotaoOAuthConnect>
          ) : (
            <BotaoOAuthDisconnect
              titulo="Desconectar API"
              mensagem={confirmMessages.disconnect}
              funcao={handleDisconnect}
              disabled={isConnecting}
            >
              <CloudOff style={{ marginRight: '8px' }} />
              {isConnecting ? 'Desconectando...' : buttonText.disconnect}
            </BotaoOAuthDisconnect>
          )}
        </ButtonContainer>
      </StatusButtonRow>

      <OAuthConnectionMonitor
        isOpen={showMonitor}
        onConnectionSuccess={handleConnectionSuccess}
        connectionState={connectionState}
      />
    </ConnectorContainer>
  );
};

export default OAuthConnector;
